import React, {
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
} from "react";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Divider,
  OutlinedInput,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  TrendingUp,
  CreditCard,
  Message,
  AccountBalanceWallet,
  Public,
  Receipt,
  Settings,
  Download,
  Add,
  Visibility,
  CalendarToday,
  AttachMoney,
  ArrowUpward,
  Info,
  Co2Sharp,
  Close as CloseIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useAuth } from "../../hooks/AuthProvider";
import { GET_TENANT_DETAILS } from "../../config/urls";
import ButtonOne from "../../common/normal-button";
import typography from "../../cssUtils/typography";
import { CallingCodeSelect } from "../../common/country-code-select";
import { getCountryForCallingCode, splitPhoneNumber } from "../../common/country-codes";
import { getPhoneValidationError } from "../../common/phone-validation";
import { ColorsConstants } from "../../cssUtils/constants";



export default function SettingsTab() {
  const [formattedPlan, setFormattedPlan] = useState("");
  const [tenantDetails, setTenantDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const { getValidToken } = useAuth();

  // Phone number state (split into country code and number)
  const [countryCode, setCountryCode] = useState("91"); // Default to India
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");

  // Form state for controlled inputs with safe defaults
  const [formData, setFormData] = useState({
    name: "",
    gstin: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    place_of_supply: "",
    phone: "",
    email: "",
    email_2: "",
    email_3: "",
    company: "",
    billing_currency: "" // Added billing currency to form data
  });

  const getPhoneError = (code, phone) =>
    phone ? getPhoneValidationError(phone, getCountryForCallingCode(code), code) : null; // Optional field

  const handleCountryCodeChange = (code) => {
    setCountryCode(code);
    setPhoneError(getPhoneError(code, phoneNumber) || "");
  };

  // Add effect to parse existing phone number when tenant details are loaded
  useEffect(() => {
    if (formData.phone) {
      parseExistingPhoneNumber(formData.phone);
    }
  }, [formData.phone]);

  const parseExistingPhoneNumber = (phone) => {
    const value = String(phone || "").trim();
    // A bare 10-digit number is an Indian number without its country code
    const { countryCode: code, phone: number } =
      /^\d{10}$/.test(value) ? { countryCode: "", phone: value } : splitPhoneNumber(value);
    setCountryCode(code || "91");
    setPhoneNumber(code ? number : value);
  };

  // Update formData.phone when country code or phone number changes
  useEffect(() => {
    if (phoneNumber && validatePhone()) {
      const fullPhone = `+${countryCode}${phoneNumber}`;
      handleInputChange("phone", fullPhone);
    } else {
      handleInputChange("phone", "");
    }
  }, [countryCode, phoneNumber]);

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setPhoneNumber(value);
    setPhoneError(getPhoneError(countryCode, value) || "");
  };

  const validatePhone = () => !getPhoneError(countryCode, phoneNumber);

  // Plan details configuration with error fallback
  const planDetails = {
    whatsapp_lite: {
      name: "WhatsApp Lite",
      subtitle: "WhatsApp Charges as per usage",
      cost: "As per Agreement",
      features: [
        "Upto 3 Agents/Admin user access",
        "Audience and Customer Segmentation",
        "Upload Audience data",
        "WhatsApp Marketing suite",
        "Agent Live Chat",
        "Shared Live Chat Inbox",
        "Upto 2 standard Reports",
        "Email Support",
        "Broadcast Marketing Message",
        "• All contacts from Audience",
        "• Specific Audience Segment", 
        "• Manually upload Phone numbers",
        "Helpful tools",
        "• QR code generator",
        "• WhatsApp link generator",
        "• Chat Widget",
      ]
    },
    whatsapp_advanced: {
      name: "WhatsApp Advanced",
      subtitle: "WhatsApp Charges as per usage",
      cost: "As per Agreement", 
      features: [
        "Everything in WhatsApp Lite",
        "Upto 5 Agents/Admin users",
        "Role based login (2 roles)",
        "WhatsApp Catalog",
        "GenAI Live Agent",
        "GenAI assistance for Marketing Templates",
        "Upto 5 standard Reports",
        "Upto 2 chatbots configured",
        "Upto 3 iterations of Bot flow changes",
        "WhatsApp Commerce integration (any one)",
        "• Shopify",
        "• Magento",
      ]
    },
    whatsapp_advanced_custom_solution: {
      name: "WhatsApp Custom ",
      subtitle: "WhatsApp Charges as per usage",
      cost: "As per Agreement",
      features: [
        "Everything in WhatsApp Advanced",
        "Upto 8 Agents/Admin users",
        "Role based login (3 roles)",
        "Upto 2 API based integrations with external system",
        "Upto 7 standard and custom reports",
        "Email & Phone support",
        "Dedicated Account Manager",
        "Custom backend (Any one)",
        "• Appointment booking system",
        "• Order placement and Catalogue",
        "• Custom CRM",
      ]
    },
    enterprise_whatsapp_solution: {
      name: "Enterprise WhatsApp Solution",
      subtitle: "WhatsApp Charges as per usage",
      cost: "Custom Pricing",
      features: [
        "Unlimited Agents/admin users",
        "Role based login (3 roles)",
        "Fully Customized backend",
        "Multiple integrations", 
        "Advanced AI solutions",
        "Email & Phone support",
        "Priority support",
        "Dedicated Account Manager"
      ]
    },
    // Fallback plan for when no plan is found
    default: {
      name: "No Plan Selected",
      subtitle: "Please contact support",
      cost: "N/A",
      features: ["Please contact support for plan details"]
    }
  };

  useEffect(() => {
    fetchTenantDetails();
  }, []);

  const formatPlanName = (planName) => {
    try {
      const map = {
        whatsapp_lite: "WhatsApp Lite",
        whatsapp_advanced: "WhatsApp Advanced",
        whatsapp_advanced_custom_solution: "WhatsApp Advanced Custom Solution",
        enterprise_whatsapp_solution: "Enterprise WhatsApp Solution",
      };
      
      if (!planName) return "No Plan Selected";
      
      return (
        map[planName] ||
        planName
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      );
    } catch (error) {
      console.warn("Error formatting plan name:", error);
      return "Unknown Plan";
    }
  };

  useEffect(() => {
    try {
      const plan = localStorage.getItem("plan");
      if (plan) {
        const newPlan = formatPlanName(plan);
        setFormattedPlan(newPlan);
      } else {
        setFormattedPlan("No Plan Selected");
      }
    } catch (error) {
      console.warn("Error accessing localStorage for plan:", error);
      setFormattedPlan("Unknown Plan");
    }
  }, []);

  // Get current plan details with safe fallback
  const getCurrentPlanDetails = () => {
    try {
      const plan = localStorage.getItem("plan");
      return planDetails[plan] || planDetails.default;
    } catch (error) {
      console.warn("Error getting current plan details:", error);
      return planDetails.default;
    }
  };

  const apiRequest = async (url, options = {}) => {
    try {
      // Validate required dependencies
      if (!getValidToken) {
        throw new Error("Authentication provider not available");
      }

      const token = await getValidToken();
      if (!token) {
        throw new Error("Unable to obtain valid authentication token");
      }

      const tenantId = localStorage.getItem("tenant");
      if (!tenantId) {
        throw new Error("Tenant ID not found in localStorage");
      }

      if (!url) {
        throw new Error("API URL is required");
      }

      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": tenantId,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`API request failed with status: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("API Request Error:", error);
      throw error;
    }
  };

  const fetchTenantDetails = async () => {
    try {
      setLoading(true);
      setApiError(null);
      
      if (!GET_TENANT_DETAILS) {
        throw new Error("Tenant details API URL not configured");
      }

      const data = await apiRequest(GET_TENANT_DETAILS);
      
      // Safely extract tenant details with fallback
      const tenantD = data?.results?.[0] || {};
      setTenantDetails(tenantD);
      
      // Initialize form data with fetched values and safe defaults
      setFormData({
        name: tenantD?.name || "",
        gstin: tenantD?.gstin || "",
        address: tenantD?.address || "",
        city: tenantD?.city || "",
        state: tenantD?.state || "",
        country: tenantD?.country || "",
        postal_code: tenantD?.postal_code || "",
        place_of_supply: tenantD?.place_of_supply || "",
        phone: tenantD?.phone || "",
        email: tenantD?.email || "",
        email_2: tenantD?.email_2 || "",
        email_3: tenantD?.email_3 || "",
        company: tenantD?.company || "",
        billing_currency: tenantD?.billing_currency || "INR" // Default to INR if not provided
      });
      
      console.log("Tenant Details:", tenantD?.name || "No name available");
    } catch (error) {
      console.error("Error fetching tenant details:", error);
      setApiError(error.message);
      setSnackbar({
        open: true,
        message: `Failed to fetch tenant details: ${error.message}`,
        severity: "error"
      });
      
      // Set empty tenant details to prevent crashes
      setTenantDetails({});
    } finally {
      setLoading(false);
    }
  };

  // Retry mechanism for failed API calls
  const retryFetchTenantDetails = () => {
    setRetryCount(prev => prev + 1);
    fetchTenantDetails();
  };

  // Validation functions with error handling
  const validateGSTIN = (gstin) => {
    try {
      if (!gstin) return true; // Optional field
      const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      return gstinRegex.test(gstin);
    } catch (error) {
      console.warn("Error validating GSTIN:", error);
      return false;
    }
  };

  const validatePincode = (pincode) => {
    try {
      if (!pincode) return true; // Optional field
      const pincodeRegex = /^[1-9][0-9]{5}$/;
      return pincodeRegex.test(pincode);
    } catch (error) {
      console.warn("Error validating pincode:", error);
      return false;
    }
  };

  const validateEmail = (email) => {
    try {
      if (!email) return true; // Optional field
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    } catch (error) {
      console.warn("Error validating email:", error);
      return false;
    }
  };

  const validateForm = () => {
    try {
      const newErrors = {};

      // Required field validations
      if (!formData.name?.trim()) {
        newErrors.name = "Company name is required";
      }

      if (!formData.address?.trim()) {
        newErrors.address = "Address is required";
      }

      if (!formData.city?.trim()) {
        newErrors.city = "City is required";
      }

      if (!formData.state?.trim()) {
        newErrors.state = "State is required";
      }

      // GSTIN validation (optional but must be valid if provided)
      if (formData.gstin && !validateGSTIN(formData.gstin)) {
        newErrors.gstin = "Invalid GSTIN format. Format: 22AAAAA0000A1Z5";
      }

      // Pincode validation
      if (formData.postal_code && !validatePincode(formData.postal_code)) {
        newErrors.postal_code = "Invalid pincode. Must be 6 digits and not start with 0";
      }

      // Email validation (optional but must be valid if provided)
      if (formData.email && !validateEmail(formData.email)) {
        newErrors.email = "Invalid email format";
      }

      if (formData.email_2 && !validateEmail(formData.email_2)) {
        newErrors.email_2 = "Invalid email format for Email 2";
      }

      if (formData.email_3 && !validateEmail(formData.email_3)) {
        newErrors.email_3 = "Invalid email format for Email 3";
      }

      // Phone validation (optional but must be valid if provided)
      if (phoneNumber && !validatePhone()) {
        newErrors.phone = phoneError || "Invalid phone number format";
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    } catch (error) {
      console.error("Error during form validation:", error);
      setSnackbar({
        open: true,
        message: "Error validating form data",
        severity: "error"
      });
      return false;
    }
  };

  const handleInputChange = (field, value) => {
    try {
      if (!field) {
        console.warn("Field name is required for input change");
        return;
      }

      setFormData(prev => ({
        ...prev,
        [field]: value || ""
      }));

      // Clear error for this field when user starts typing
      if (errors[field]) {
        setErrors(prev => ({
          ...prev,
          [field]: ""
        }));
      }
    } catch (error) {
      console.error("Error handling input change:", error);
      setSnackbar({
        open: true,
        message: "Error updating form field",
        severity: "error"
      });
    }
  };

  const updateTenantDetails = async (e) => {
    try {
      e?.preventDefault(); // Prevent form submission reload
      
      if (!validateForm()) {
        setSnackbar({
          open: true,
          message: "Please fix the validation errors",
          severity: "error"
        });
        return;
      }

      if (!tenantDetails?.id) {
        setSnackbar({
          open: true,
          message: "Unable to update: Tenant details not loaded",
          severity: "error"
        });
        return;
      }

      setSaving(true);
      
      // Construct the update URL with safe fallback
      const updateUrl = `${GET_TENANT_DETAILS || ''}${tenantDetails.id}/`;
      
      if (!updateUrl || updateUrl === '/') {
        throw new Error("Invalid update URL configuration");
      }
      
      // Prepare data - convert empty strings to null for email fields
      const dataToSend = {
        ...formData,
        // Convert empty email strings to null
        email_2: formData.email_2?.trim() === "" ? null : formData.email_2?.trim(),
        email_3: formData.email_3?.trim() === "" ? null : formData.email_3?.trim(),
        // Also handle primary email (optional - depends on your requirements)
        // email: formData.email?.trim() === "" ? null : formData.email?.trim(),
      };
      
      console.log("Sending data:", dataToSend);
      
      await apiRequest(updateUrl, {
        method: "PUT",
        body: JSON.stringify(dataToSend),
      });

      setSnackbar({
        open: true,
        message: "Tenant details updated successfully!",
        severity: "success"
      });

      // Refresh the data
      await fetchTenantDetails();

    } catch (error) {
      console.error("Error updating tenant details:", error);
      setSnackbar({
        open: true,
        message: `Failed to update tenant details: ${error.message}`,
        severity: "error"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    try {
      setSnackbar(prev => ({ ...prev, open: false }));
    } catch (error) {
      console.warn("Error closing snackbar:", error);
    }
  };

  // Error boundary component for features rendering
  const SafeFeaturesList = ({ features = [] }) => {
    try {
      if (!Array.isArray(features) || features.length === 0) {
        return (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No features available
          </Typography>
        );
      }

      // Group features with their sub-bullets
      const groupedFeatures = [];
      let currentGroup = [];
      
      for (let i = 0; i < features.length; i++) {
        const feature = features[i];
        
        if (typeof feature !== 'string') {
          console.warn("Invalid feature format:", feature);
          continue;
        }
        
        if (feature.startsWith('•')) {
          // This is a sub-bullet, add to current group
          currentGroup.push(feature);
        } else {
          // This is a main feature
          if (currentGroup.length > 0) {
            // Save the previous group
            groupedFeatures.push([...currentGroup]);
          }
          // Start a new group with this main feature
          currentGroup = [feature];
        }
      }
      
      // Don't forget the last group
      if (currentGroup.length > 0) {
        groupedFeatures.push(currentGroup);
      }
      
      if (groupedFeatures.length === 0) {
        return (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No valid features found
          </Typography>
        );
      }
      
      // Distribute groups across 3 columns
      const itemsPerColumn = Math.ceil(groupedFeatures.length / 3);
      const columns = [];
      
      for (let i = 0; i < 3; i++) {
        const startIndex = i * itemsPerColumn;
        const endIndex = Math.min(startIndex + itemsPerColumn, groupedFeatures.length);
        columns.push(groupedFeatures.slice(startIndex, endIndex));
      }
      return columns.map((columnGroups, columnIndex) => (
        <Grid item xs={12} md={4} key={columnIndex}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {columnGroups.map((group, groupIndex) => (
              <Box key={groupIndex} sx={{ mb: 2 }}>
                {group.map((feature, featureIndex) => {
                  const isBulletPoint = feature.startsWith('•');
                  
                  return (
                    <Box
                      key={featureIndex}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        mb: 0.75,
                        pl: isBulletPoint ? 2 : 0,
                      }}
                    >
                      {/* Show checkmark only for main features, bullet for sub-features */}
                      {isBulletPoint ? (
                        <Typography
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                            mt: 0.2,
                            mr: 0.5,
                            minWidth: "auto",
                          }}
                        >
                          •
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            backgroundColor: "primary.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            mt: 0.2,
                          }}
                        >
                          <Typography
                            sx={{
                              color: "white",
                              fontSize: 10,
                              fontWeight: "bold",
                            }}
                          >
                            ✓
                          </Typography>
                        </Box>
                      )}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontWeight: isBulletPoint ? 'normal' : 'medium',
                          fontSize: isBulletPoint ? '0.75rem' : '0.875rem',
                          lineHeight: 1.4,
                          flex: 1,
                        }}
                      >
                        {isBulletPoint ? feature.substring(1).trim() : feature}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Grid>
      ));
    } catch (error) {
      console.error("Error rendering features:", error);
      return (
        <Grid item xs={12}>
          <Typography variant="body2" color="error">
            Error loading features. Please refresh the page.
          </Typography>
        </Grid>
      );
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          mt: 3,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading tenant details...
        </Typography>
      </Box>
    );
  }

  // Error state with retry option
  if (apiError && Object.keys(tenantDetails).length === 0) {
    return (
      <Box sx={{ mt: 3 }}>
        <Card>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                py: 4,
              }}
            >
              <ErrorIcon color="error" sx={{ fontSize: 48 }} />
              <Typography variant="h6" color="error" align="center">
                Failed to Load Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                {apiError}
              </Typography>
              <Button
                variant="contained"
                onClick={retryFetchTenantDetails}
                startIcon={<RefreshIcon />}
                disabled={loading}
              >
                {loading ? "Retrying..." : "Retry"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const currentPlan = getCurrentPlanDetails();

  return (
    <Box sx={{ mt: 3, px: 3, px: 1 }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* API Error Banner */}
      {apiError && Object.keys(tenantDetails).length > 0 && (
        <Alert 
          severity="warning" 
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={retryFetchTenantDetails}>
              Retry
            </Button>
          }
        >
          Some data may be outdated due to API error: {apiError}
        </Alert>
      )}


      {/* Company Information */}
      <Card>
        <CardContent>
          <Typography sx={{ ...typography.h1, mb: 3}}>
            Company Information
          </Typography>

          {Object.keys(tenantDetails).length === 0 && !loading ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No company information available. You can still add new details below.
            </Alert>
          ) : null}

          <Box
            component="form"
            onSubmit={updateTenantDetails}
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            {/* Basic Company Info */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={formData.name || ""}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  variant="outlined"
                  required
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GSTIN"
                  value={formData.gstin || ""}
                  onChange={(e) => handleInputChange("gstin", e.target.value.toUpperCase())}
                  variant="outlined"
                  error={!!errors.gstin}
                  helperText={errors.gstin || "Format: 22AAAAA0000A1Z5"}
                  placeholder="22AAAAA0000A1Z5"
                />
              </Grid>
            </Grid>

            {/* Contact Information */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={formData.email || ""}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  variant="outlined"
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {/* Phone Number Fields - Updated to match SingleContact.js */}
                {/* <Typography variant="subtitle1" sx={{ mb: 1 }} gutterBottom>
                  Phone Number {phoneNumber ? "" : "(Optional)"}
                </Typography> */}
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <CallingCodeSelect value={countryCode} onChange={handleCountryCodeChange} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      variant="outlined"
                      value={phoneNumber}
                      onChange={handlePhoneNumberChange}
                      error={!!errors.phone || !!phoneError}
                      helperText={errors.phone || phoneError || ""}                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Secondary Email (Optional)"
                  value={formData.email_2 || ""}
                  onChange={(e) => handleInputChange("email_2", e.target.value)}
                  variant="outlined"
                  type="email"
                  error={!!errors.email_2}
                  helperText={errors.email_2}
                  placeholder="Optional"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tertiary Email (Optional)"
                  value={formData.email_3 || ""}
                  onChange={(e) => handleInputChange("email_3", e.target.value)}
                  variant="outlined"
                  type="email"
                  error={!!errors.email_3}
                  helperText={errors.email_3}
                  placeholder="Optional"
                />
              </Grid>
            </Grid>

            {/* Billing Address */}
            <Box>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Billing Address
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={formData.address || ""}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  variant="outlined"
                  required
                  error={!!errors.address}
                  helperText={errors.address}
                  multiline
                  rows={2}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.city || ""}
                      onChange={(e) => handleInputChange("city", e.target.value)}
                      variant="outlined"
                      required
                      error={!!errors.city}
                      helperText={errors.city}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="PIN Code"
                      value={formData.postal_code || ""}
                      onChange={(e) => handleInputChange("postal_code", e.target.value)}
                      variant="outlined"
                      error={!!errors.postal_code}
                      helperText={errors.postal_code}
                      placeholder="110001"
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="State"
                      value={formData.state || ""}
                      onChange={(e) => handleInputChange("state", e.target.value)}
                      variant="outlined"
                      required
                      error={!!errors.state}
                      helperText={errors.state}
                      placeholder="Maharashtra"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={formData.country || ""}
                      variant="outlined"
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          color: 'text.secondary',
                          backgroundColor: 'action.disabledBackground',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'action.disabled',
                        }
                      }}
                      helperText="Country cannot be changed from UI"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Place of Supply and Billing Currency */}
            <Box>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Billing Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Place of Supply"
                    value={formData.place_of_supply || ""}
                    onChange={(e) => handleInputChange("place_of_supply", e.target.value)}
                    variant="outlined"
                    error={!!errors.place_of_supply}
                    helperText={errors.place_of_supply}
                    placeholder="Maharashtra"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Billing Currency"
                    value={formData.billing_currency || ""}
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: 'text.secondary',
                        backgroundColor: 'action.disabledBackground',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'action.disabled',
                      }
                    }}
                    helperText="Billing currency cannot be changed from UI"
                  />
                </Grid>
              </Grid>
            </Box>

            <ButtonOne
              type="submit"
              sx={{ alignSelf: "flex-start" }}
              disabled={saving || Object.keys(tenantDetails).length === 0}
              startIcon={saving ? <CircularProgress size={20} /> : null}
            >
              {saving ? "Saving..." : "Save Changes"}
            </ButtonOne>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}